/*
 * E-Waste Collection Drive — categories and school items.
 *
 * Categories: Appendix-1 B1 of the DARPG guidelines for Special Campaign 6
 * (Schedule I, E-Waste (Management) Rules, 2022) — ITEW, EETW, TLSEW, LIW, OTH.
 * Batteries, tube lights / bulbs, fans and UPS / inverter are reported under
 * EETW; the ATL kits and the 3D printer sit with the laboratory instruments.
 *
 * ITEW, EETW, TLSEW and LIW list items and end with "Other" (named by the
 * school). OTH has no sub-categories: the school adds named rows, and only
 * electrical / electronic items are accepted there (isEwasteName below).
 *
 * Keys are stored. Backend2/data/ewasteCatalog.js must carry the same category
 * and item keys, the same LEGACY_ITEM_MOVES and the same name rules (labels
 * live only here).
 */

export const EWASTE_CATEGORIES = [
  {
    code: "ITEW",
    title: "Information Technology & Telecommunication Equipment",
    short: "IT & Telecom",
    tone: "#2563eb",
    items: [
      { key: "desktop", label: "Desktop computer" },
      { key: "cpu", label: "CPU cabinet (if not reported as full desktop)" },
      { key: "laptop", label: "Laptop / notebook" },
      { key: "tablet", label: "Tablet" },
      { key: "monitor", label: "Computer monitor (CRT / LCD / LED)" },
      { key: "peripherals", label: "Keyboard, mouse, webcam & speakers" },
      { key: "printer", label: "Printer & ink / toner cartridges" },
      { key: "scanner", label: "Scanner, photocopier & fax machine" },
      { key: "projector", label: "Projector / smart-class display panel" },
      { key: "television", label: "Television / display panel" },
      { key: "settop", label: "Set-top box & TV tuner" },
      { key: "audio", label: "Speaker, microphone & PA system" },
      { key: "cctv", label: "CCTV camera & DVR" },
      { key: "biometric", label: "Biometric attendance machine" },
      { key: "network", label: "Router, modem & network switch" },
      { key: "phone", label: "Mobile phone & landline / cordless phone" },
      { key: "storage", label: "Hard disk, pen drive, CD / DVD" },
      { key: "cables", label: "Cables, chargers, adapters & earphones" },
      { key: "other", label: "Other" },
    ],
  },
  {
    code: "EETW",
    title: "Electrical & Electronic Tools",
    short: "Electrical & Tools",
    tone: "#d97706",
    items: [
      { key: "batteries", label: "Batteries — dry cells, button cells, UPS / inverter, laptop & mobile packs" },
      { key: "ups", label: "UPS & inverter" },
      { key: "lights", label: "Tube lights, CFL & LED bulbs" },
      { key: "fans", label: "Fans" },
      { key: "fridge", label: "Refrigerator" },
      { key: "water", label: "Water purifier / RO & water cooler" },
      { key: "cooler", label: "Air cooler / air conditioner" },
      { key: "kitchen", label: "Mixer-grinder, kettle & kitchen appliances" },
      { key: "washing", label: "Washing machine" },
      { key: "bell", label: "Electric bell, siren & emergency light" },
      { key: "health", label: "Thermometer, BP monitor & nebuliser" },
      { key: "detector", label: "Smoke detector & fire alarm panel" },
      { key: "drill", label: "Electric drill / screwdriver" },
      { key: "soldering", label: "Soldering iron & welding kit" },
      { key: "cutter", label: "Electric saw, grinder & cutter" },
      { key: "glue_gun", label: "Hot glue gun" },
      { key: "sewing", label: "Electric sewing machine" },
      { key: "heat_gun", label: "Heat gun / electric blower" },
      { key: "garden", label: "Lawn mower, hedge trimmer & garden tools" },
      { key: "other", label: "Other" },
    ],
  },
  {
    code: "TLSEW",
    title: "Toys, Leisure & Sports Equipment",
    short: "Toys & Sports",
    tone: "#db2777",
    items: [
      { key: "toys", label: "Battery-operated / remote-control toys" },
      { key: "rideon", label: "Battery-operated ride-on toy" },
      { key: "drone", label: "Drones & RC models" },
      { key: "gaming", label: "Video game consoles & handheld games" },
      { key: "music", label: "Electric musical instrument / keyboard" },
      { key: "scoreboard", label: "Electronic scoreboard, stopwatch & timer" },
      { key: "fitness", label: "Treadmill / fitness equipment with electronics" },
      { key: "other", label: "Other" },
    ],
  },
  {
    code: "LIW",
    title: "Laboratory Instruments",
    short: "Lab Instruments",
    tone: "#7c3aed",
    items: [
      { key: "balance", label: "Digital weighing balance" },
      { key: "microscope", label: "Electric / digital microscope" },
      { key: "meters", label: "Multimeter, voltmeter & ammeter" },
      { key: "ph_meter", label: "pH, conductivity & TDS meter" },
      { key: "heating", label: "Laboratory hot plate, water bath & oven" },
      { key: "stirrer", label: "Centrifuge & magnetic stirrer" },
      { key: "power_supply", label: "Power supply / battery eliminator" },
      { key: "sensors", label: "Digital thermometer, sensors & data loggers" },
      { key: "robotics", label: "Robotics & electronics kits (ATL)" },
      { key: "printer3d", label: "3D printer / laser cutter (ATL)" },
      { key: "other", label: "Other" },
    ],
  },
  {
    code: "OTH",
    title: "Others",
    short: "Others",
    tone: "#0f766e",
    // No sub-categories: the school types each item's name and quantity.
    freeform: true,
    items: [{ key: "other", label: "Other electrical / electronic item (specify)" }],
  },
];

// ── Items that changed category (2026-10-01) ───────────────────────────────
// The item keys did not change — only the category they belong to. Reports
// saved before the change are read (and migrated) through this map, so an old
// ATL robotics kit counts under Lab Instruments like a new one.
export const LEGACY_ITEM_MOVES = {
  "TLSEW:robotics": "LIW",
  "EETW:printer3d": "LIW",
  "LIW:detector": "EETW",
  "ITEW:ups": "EETW",
};

export const remapEwasteCategory = (category, item) => LEGACY_ITEM_MOVES[`${category}:${item}`] || category;

export const EWASTE_ITEM_LABEL = Object.fromEntries(
  EWASTE_CATEGORIES.flatMap((c) => c.items.map((it) => [`${c.code}:${it.key}`, it.label]))
);

// ── "Other" must be e-waste ────────────────────────────────────────────────
// Schools typed fruit, paper and plastic into the Others rows, so a name is
// accepted only when it names something electrical or electronic. Roman words
// are matched on word boundaries (with an optional plural) and the Hindi terms
// as plain substrings. Keep this list in step with Backend2/data/ewasteCatalog.js.
const EWASTE_NAME_WORDS = [
  "electric", "electrical", "electronic", "electronics", "ewaste", "e-waste", "gadget", "device", "appliance", "machine", "equipment", "instrument",
  "battery", "batteries", "cell", "dry cell", "button cell", "power bank", "powerbank", "solar", "panel", "torch", "flashlight", "emergency light", "lantern",
  "bulb", "cfl", "led", "tube", "tubelight", "tube light", "lamp", "light", "chandelier", "choke", "starter", "ballast",
  "fan", "exhaust", "cooler", "ac", "air conditioner", "refrigerator", "fridge", "freezer", "geyser", "heater", "iron", "kettle", "oven", "microwave", "toaster", "mixer", "grinder", "juicer", "blender", "induction", "hot plate", "hotplate", "chimney",
  "water purifier", "purifier", "ro", "water cooler", "dispenser", "washing machine", "vacuum", "cleaner",
  "computer", "cpu", "desktop", "laptop", "notebook", "tablet", "monitor", "screen", "display", "keyboard", "mouse", "webcam", "speaker", "mic", "microphone", "headphone", "earphone", "headset", "amplifier", "woofer",
  "printer", "cartridge", "toner", "scanner", "photocopier", "xerox", "fax", "projector", "smartboard", "smart board",
  "ups", "inverter", "stabilizer", "transformer", "adapter", "adaptor", "charger", "cable", "wire", "cord", "plug", "socket", "switch", "switchboard", "extension", "mcb", "holder",
  "router", "modem", "network", "lan", "wifi", "antenna", "dish", "set top", "settop", "setup box", "tv", "television", "dvd", "vcd", "dvr", "cctv", "camera", "radio", "tape", "recorder",
  "phone", "mobile", "smartphone", "telephone", "landline", "cordless", "intercom", "walkie", "pager",
  "hard disk", "harddisk", "hdd", "ssd", "pendrive", "pen drive", "usb", "floppy", "memory card", "sim", "cd",
  "calculator", "clock", "watch", "timer", "stopwatch", "scoreboard", "remote", "bell", "siren", "alarm", "buzzer", "biometric", "attendance",
  "motor", "pump", "generator", "drill", "welding", "solder", "soldering", "cutter", "saw", "blower", "heat gun", "glue gun", "sewing", "trimmer", "mower", "compressor",
  "sensor", "meter", "multimeter", "voltmeter", "ammeter", "galvanometer", "oscilloscope", "balance", "weighing", "microscope", "centrifuge", "stirrer", "incubator", "autoclave", "data logger", "ph meter", "tds", "conductivity", "thermometer", "oximeter", "nebuliser", "nebulizer", "bp monitor", "stimulator",
  "robot", "robotics", "arduino", "raspberry", "circuit", "pcb", "chip", "transistor", "resistor", "capacitor", "diode", "motherboard", "smps", "kit",
  "drone", "rc", "console", "playstation", "xbox", "joystick", "gaming", "treadmill", "keyboard", "piano", "synthesizer", "guitar", "toy car",
  "smoke detector", "detector", "fire alarm", "detector panel",
];
const EWASTE_NAME_OTHER = [
  "बैटरी", "बिजली", "इलेक्ट्रॉनिक", "इलेक्ट्रिक", "पंखा", "बल्ब", "ट्यूब", "लाइट", "तार", "चार्जर", "मोबाइल", "कंप्यूटर", "कम्प्यूटर", "टीवी", "टी.वी", "मशीन", "इस्त्री", "हीटर", "घड़ी", "रेडियो", "प्रिंटर", "कैमरा", "मोटर", "सेल",
];
const escapeRx = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const EWASTE_NAME_RX = new RegExp(`(^|[^a-z0-9])(${EWASTE_NAME_WORDS.map(escapeRx).join("|")})(s|es)?([^a-z0-9]|$)`, "i");

export const isEwasteName = (name) => {
  const s = String(name || "").replace(/\s+/g, " ").trim();
  if (s.length < 2) return false;
  if (EWASTE_NAME_OTHER.some((w) => s.includes(w))) return true;
  return EWASTE_NAME_RX.test(s.toLowerCase());
};

export const EWASTE_ONLY_MSG = "Only E-Waste (electrical or electronic) items can be entered here.";
export const notEwasteMsg = (name) => `“${String(name).slice(0, 40)}” is not E-Waste. ${EWASTE_ONLY_MSG} Enter an electrical or electronic item such as a battery, charger, bulb, fan or mobile phone.`;

export const CO_CURRICULAR = [
  { key: "poster", label: "Poster making" },
  { key: "essay", label: "Essay writing" },
  { key: "art_installation", label: "E-Waste art installation" },
  { key: "quiz", label: "Quiz" },
  { key: "rally", label: "Awareness rally" },
  { key: "other", label: "Other" },
];

export const AGE_GROUPS = [
  { key: "age3to8", label: "3 to 8" },
  { key: "age8to11", label: "8 to 11" },
  { key: "age11to14", label: "11 to 14" },
  { key: "age14to18", label: "14 to 18" },
  { key: "age18plus", label: "18+ (Teachers)", teacher: true },
];

export const EWASTE_LIMITS = {
  quantityMax: 100000,
  countMax: 1000000,
  workshopsMax: 1000,
  descriptionMin: 10,
  descriptionMax: 500,
  otherNameMax: 80,
  othRowsMax: 10,
  photosMin: 1,
  photosMax: 5,
  fileMaxMb: 5,
};
